import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Icon } from "../..";
import { usePaymentMethods } from "../../../../hooks";

const PaymentMethodsStep = ({
  isEditing = false,
  selectedPaymentMethods,
  onPaymentMethodToggle,
  priceLevel,
  onPriceLevelChange,
  isAddingPaymentMethod,
  addPaymentMethod,
  errors,
}) => {
  const { t } = useTranslation("stores");
  const { paymentMethods, isLoading } = usePaymentMethods();
  const paymethodsToInclude = [
    "cash",
    "card_delivery",
    "stripe_connect_link_finitless",
  ];

  const checkEnableState = (id) => {
    if (!isEditing) {
      return {
        isEnabled: selectedPaymentMethods.includes(id),
      };
    }
    const isEnabled = !!selectedPaymentMethods?.find(
      (paymethod) => paymethod.paymethod_id === id && paymethod.enabled,
    );
    const paymethod = selectedPaymentMethods?.find(
      (paymethod) => paymethod.paymethod_id === id,
    );
    return {
      isEnabled,
      paymethod,
    };
  };

  const handlePaymentMethodToggle = (id) => {
    if (!isEditing) {
      return onPaymentMethodToggle(id);
    }
    if (!checkEnableState(id).paymethod?.id) {
      addPaymentMethod({ paymethod_id: id });
      return;
    }
    onPaymentMethodToggle(
      checkEnableState(id).paymethod?.id,
      !checkEnableState(id).isEnabled,
    );
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Price Level */}
      <div>
        <label className="mb-3 block text-sm font-medium text-gray-300">
          Price Level
          <span className="ml-1 text-red-500">*</span>
        </label>
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
          {[1, 2, 3, 4, 5].map((level) => (
            <button
              key={level}
              onClick={() => onPriceLevelChange(level.toString())}
              className={`rounded-lg border-2 p-3 transition-colors sm:p-4 ${
                priceLevel === level.toString()
                  ? "border-blue-500 bg-blue-500/20 text-blue-400"
                  : "border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500"
              }`}
            >
              <div className="text-center">
                <div className="text-base font-bold sm:text-lg">
                  {"$".repeat(level)}
                </div>
                <div className="text-xs">Level {level}</div>
              </div>
            </button>
          ))}
        </div>
        {errors?.price_level && (
          <p className="mt-2 text-sm text-red-500">{errors?.price_level}</p>
        )}
      </div>

      {/* Payment Methods */}
      <div>
        <label className="mb-3 block text-sm font-medium text-gray-300">
          Payment Methods
          <span className="ml-1 text-red-500">*</span>
        </label>
        {isLoading ? (
          <div className="flex items-center justify-center py-6 sm:py-8">
            <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-blue-500 sm:h-8 sm:w-8"></div>
            <span className="ml-2 text-sm text-gray-400 sm:text-base">
              Loading payment methods...
            </span>
          </div>
        ) : paymentMethods?.length === 0 ? (
          <div className="py-6 text-center sm:py-8">
            <Icon
              name="AlertCircle"
              className="mx-auto mb-2 h-6 w-6 text-gray-400 sm:h-8 sm:w-8"
            />
            <p className="text-sm text-gray-400 sm:text-base">
              No payment methods available
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            {paymentMethods
              ?.filter((method) => paymethodsToInclude.includes(method.gateway))
              .map((method) => {
                const isDisabledPaymethod = !method.enabled;
                return (
                  <button
                    key={method.id}
                    onClick={() =>
                      !isAddingPaymentMethod &&
                      !isDisabledPaymethod &&
                      handlePaymentMethodToggle(method.id)
                    }
                    disabled={isDisabledPaymethod}
                    className={`rounded-lg border-2 p-3 text-left transition-colors sm:p-4 ${
                      isDisabledPaymethod
                        ? "cursor-not-allowed border-gray-700 bg-gray-800 opacity-50"
                        : `cursor-${isAddingPaymentMethod ? "not-allowed" : "pointer"} ${
                            checkEnableState(method.id).isEnabled
                              ? "border-blue-500 bg-blue-500/20 text-blue-400"
                              : "border-gray-600 bg-gray-700 text-gray-300 hover:border-gray-500"
                          }`
                    }`}
                  >
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div
                        className={`h-4 w-4 rounded border-2 ${
                          !isDisabledPaymethod &&
                          checkEnableState(method.id).isEnabled
                            ? "border-blue-400 bg-blue-400"
                            : "border-gray-400"
                        }`}
                      >
                        {!isDisabledPaymethod &&
                          checkEnableState(method.id).isEnabled && (
                            <div className="flex h-full w-full items-center justify-center">
                              <Icon
                                name="Check"
                                className="h-3 w-3 text-white"
                              />
                            </div>
                          )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium sm:text-base">
                          {method.name}
                        </div>
                        {isDisabledPaymethod ? (
                          <div className="mt-1 text-xs text-gray-500">
                            {t(
                              "creationForm.steps.paymentMethods.disabledInSettings",
                            )}
                          </div>
                        ) : (
                          method.description && (
                            <div className="mt-1 line-clamp-2 text-xs text-gray-400">
                              {method.description}
                            </div>
                          )
                        )}
                      </div>
                    </div>
                  </button>
                );
              })}
            {!paymentMethods?.some(
              (method) => method.gateway === "stripe_connect_link_finitless",
            ) && (
              <Link
                to="/settings/online-payments"
                className="flex items-center space-x-2 rounded-lg border-2 border-dashed border-gray-600 p-3 text-left transition-colors hover:border-blue-500 hover:bg-blue-500/10 sm:space-x-3 sm:p-4"
              >
                <div className="flex h-4 w-4 items-center justify-center">
                  <Icon name="CreditCard" className="h-4 w-4 text-gray-400" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-gray-300 sm:text-base">
                    {t("creationForm.steps.paymentMethods.onlinePayments")}
                  </div>
                  <div className="mt-1 text-xs text-blue-400">
                    {t("creationForm.steps.paymentMethods.activateInSettings")}
                  </div>
                </div>
              </Link>
            )}
          </div>
        )}
        {errors?.paymentMethods && (
          <p className="mt-2 text-sm text-red-500">{errors?.paymentMethods}</p>
        )}
      </div>
    </div>
  );
};

export default PaymentMethodsStep;
